import { Filter, Query } from '@cubejs-client/core';
import { useCubeQuery, UseCubeQueryOptions } from '@cubejs-client/react';
import { renameKeys } from '@procureai/util';
import { assoc, defaultTo, map, pipe, pluck, prop, propIs, when } from 'ramda';
import { useParams } from 'react-router-dom';

import { SourcingProjectProposalField } from '~/common/types/cubes/SourcingProjectProposal';
import {
  SourcingProjectSupplierField,
  SourcingProjectSupplierData,
  SourcingProjectSupplierSegment,
} from '~/common/types/cubes/SourcingProjectSupplier';

// Using satisfies to ensure type inheritance in SupplierListItem
export const dimToKeyMap = {
  [SourcingProjectSupplierField.SOURCING_PROJECT_ID]: 'sourcingProjectId',
  [SourcingProjectSupplierField.SUPPLIER_ID]: 'supplierId',
  [SourcingProjectSupplierField.LOCATION_ID]: 'locationId',
  [SourcingProjectSupplierField.NAME]: 'name',
  [SourcingProjectProposalField.COUNT]: 'proposalCount',
} satisfies Record<SourcingProjectSupplierField | SourcingProjectProposalField, keyof SourcingProjectSupplierData>;

const stripDimPrefix = renameKeys<
  any,
  any,
  (obj: Record<SourcingProjectSupplierField, unknown>) => SourcingProjectSupplierData
>(dimToKeyMap);

// Autogenerates a segment mapper function for the given idField used by the `VirtualList`
// Takes the `DimSourcingProjectSupplierField`s with VirtualList's ItemData enriched type and
// remaps details to the `SourcingProjectSupplierSegment` (exteding `SourcingProjectSupplierData`)
const segmentMapper = pipe<any, SourcingProjectSupplierData, SourcingProjectSupplierSegment>(
  stripDimPrefix,
  when(propIs(String, 'supplierId'), assoc('id', prop('supplierId'))),
);
export interface UseSourcingProjectSupplierProps {
  limit?: number;
}

export const useSourcingProjectSupplier = ({ limit }: UseSourcingProjectSupplierProps) => {
  const { sourcingProjectId } = useParams() as { sourcingProjectId: string };
  const filters: Filter[] = [
    {
      member: SourcingProjectSupplierField.SOURCING_PROJECT_ID,
      operator: 'equals',
      values: [sourcingProjectId],
    },
  ];
  const query = {
    dimensions: [
      SourcingProjectSupplierField.SOURCING_PROJECT_ID,
      SourcingProjectSupplierField.SUPPLIER_ID,
      SourcingProjectSupplierField.LOCATION_ID,
      SourcingProjectSupplierField.NAME,
    ],
    measures: [SourcingProjectProposalField.COUNT],
    filters,
  };
  const supplierIdsQuery = {
    dimensions: [SourcingProjectSupplierField.SUPPLIER_ID],
    filters,
  };

  const {
    data: sourcingProjectSuppliers,
    loading: isSuppliersLoading,
    total,
  } = useFetchCubeData({ ...query, total: true, limit }, { castNumerics: true });
  const { data: supplierIds, loading: isSupplierIdsLoading } = useFetchCubeData(supplierIdsQuery);

  return {
    loading: isSuppliersLoading || isSupplierIdsLoading,
    sourcingProjectSuppliers,
    query,
    total,
    hasMore: limit ? total > limit : false,
    segmentMapper,
    supplierIds: pluck('supplierId', supplierIds),
  };
};

const useFetchCubeData = (query: Query, options?: UseCubeQueryOptions) => {
  const { isLoading: loading, resultSet } = useCubeQuery(query, options);
  const rawData = resultSet?.rawData() ?? [];
  return {
    loading,
    data: map(stripDimPrefix)(rawData),
    total: defaultTo(0, resultSet?.totalRows()),
  };
};
